import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Card, CardMedia, Box} from '@mui/material';
import HideImageIcon from '@mui/icons-material/HideImage';

import {attachmentSelector, useProtectedFileUrl} from '../data/protectedFiles.js';
import {useStorageUrl} from '../data/storageUrl.js';

const ImageCard = ({image, imageUrl, onError, ...rest}) => {
    return (
        <Card sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', ...rest}}>
            <CardMedia
                sx={{
                    aspectRatio: '16/9',
                    width: '100%',
                    borderRadius: 0.5,
                    backgroundColor: 'background.paper'
                }}
                image={imageUrl}
                onError={onError}
            >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary', opacity: 0.5}}>
                    {!image && (
                        <HideImageIcon sx={{color: 'grey.500', fontSize: 40}} />
                    )}
                </Box>
            </CardMedia>
        </Card>
    );
};

const Image = ({image, imageUrl, loaded, sx, imgRef, ...rest}) => {
    if (!image) {
        return null;
    }

    return (
        <Box
            component="img"
            ref={imgRef}
            src={imageUrl}
            sx={{
                ...sx,
                opacity: loaded ? 1 : 0
            }}
            {...rest}
        />
    );
};

/**
 * Image component with an explicit public/protected boundary.
 *
 * @param {Object} props
 * @param {Object|string} props.image - Attachment metadata, a blob/data URL, or an explicitly public URL
 * @param {Object} props.fileClient - Authenticated protected-file client
 * @param {string} props.recordPath - Owning Firestore record path for a protected attachment
 * @param {boolean} props.publicAsset - Allow public URL/Storage resolution (branding, lesson-plan assets)
 * @param {string} props.type - 'card' for card layout, default for simple image
 * @param {Object} props.sx - Style props
 */
export default function FirebaseImage(props) {
    const {image, fileClient, recordPath, publicAsset = false, storage, type, sx: incomingSx, ...rest} = props;
    const [loaded, setLoaded] = useState(false);
    const imgRef = useRef(null);

    const localUrl = typeof image === 'string' && /^(?:blob:|data:)/.test(image) ? image : null;
    const publicUrl = publicAsset
        ? (typeof image === 'string' ? image : image?.thumbnailUrl || image?.imageUrl || image?.url || null)
        : null;
    const publicPath = publicAsset && typeof image === 'object'
        ? image?.thumbnailPath || image?.filePath
        : null;
    const {url: resolvedPublicUrl, onError: handlePublicError} = useStorageUrl(storage, publicUrl, publicPath);
    const variant = typeof image === 'object' && image?.thumbnailPath ? 'thumbnail' : undefined;
    const selector = attachmentSelector(typeof image === 'object' ? image : null, recordPath, variant);
    const {url: protectedUrl} = useProtectedFileUrl(fileClient, selector);
    const imageUrl = localUrl || resolvedPublicUrl || protectedUrl;

    useEffect(() => {
        // For browser-cached images, the `load` event can fire before React
        // attaches its listener — leaving the img stuck at opacity:0. Check
        // the element directly after commit so cached hits resolve correctly.
        const img = imgRef.current;
        if (img && img.complete && img.naturalWidth > 0) {
            setLoaded(true);
        } else {
            setLoaded(false);
        }
    }, [imageUrl]);

    const {width, height} = (typeof image === 'object' ? image : {}) || {};
    const aspectRatio = width && height ? `${width}/${height}` : undefined;

    const sx = {
        ...incomingSx,
        ...!incomingSx?.height && {
            height: 'auto',
            ...(aspectRatio ? {aspectRatio} : {aspectRatio: '171 / 116'})
        }
    };

    const handleImageError = useCallback(() => {
        setLoaded(false);
        if (publicAsset) handlePublicError();
    }, [handlePublicError, publicAsset]);

    if (type === 'card') {
        return <ImageCard image={image} imageUrl={imageUrl} onError={handleImageError} sx={sx} {...rest} />;
    }

    if (!imageUrl && image) {
        return (
            <Box
                variant="rounded"
                sx={sx}
            />
        );
    }

    return <Image image={image} imageUrl={imageUrl} loaded={loaded} sx={sx} imgRef={imgRef} onLoad={() => setLoaded(true)} onError={handleImageError} {...rest} />;
}
